import {
  EnvelopeSimple,
  FacebookLogo,
  InstagramLogo,
  LinkedinLogo,
} from '@phosphor-icons/react';
import { Logo } from '@/components/Logo';

/**
 * O site institucional. Quase todos os links do rodapé apontam para lá; as
 * exceções são os termos e a privacidade, que são rotas daqui (ver `ATENDIMENTO`).
 */
const SITE = 'https://mercadounico.tech';

const MENU = [
  { label: 'Como funciona', href: `${SITE}/#como-funciona` },
  { label: 'Benefícios', href: `${SITE}/#beneficios` },
  { label: 'FAQ', href: `${SITE}/#faq` },
  { label: 'Equipe', href: `${SITE}/equipe` },
];

const ATENDIMENTO = [
  { label: 'Política de reembolso', href: `${SITE}/politicas#reembolso` },
  { label: 'Política de entrega', href: `${SITE}/politicas#entrega` },
  { label: 'Rastreamento de pedidos', href: `${SITE}/politicas#rastreamento` },
  // Estes dois são rotas DAQUI (`/termos`, `/privacidade`), e não do site:
  // são o texto que o buyer aceita no wizard de boas-vindas.
  { label: 'Termos de uso', href: '/termos' },
  { label: 'Política de privacidade', href: '/privacidade' },
];

const REDES = [
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/mercadounico/',
    Icon: InstagramLogo,
  },
  {
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/company/mercado-unico',
    Icon: LinkedinLogo,
  },
  {
    label: 'Facebook',
    href: 'https://www.facebook.com/profile.php?id=61570365454360',
    Icon: FacebookLogo,
  },
  {
    label: 'E-mail',
    href: 'mailto:contato@mercadounico.tech',
    Icon: EnvelopeSimple,
  },
];

// Sobre o fundo escuro do rodapé, o texto é o `footer-foreground` com
// opacidade — e não `muted-foreground`, que é cinza neutro e some ali. A base
// é quase branca nos dois temas, então 70% passa folgado no contraste.
const LINK =
  'text-sm text-footer-foreground/70 transition-colors hover:text-footer-foreground';

const TITULO =
  'mb-3 text-xs font-semibold uppercase tracking-wide text-footer-foreground';

/**
 * Rodapé institucional, portado do site (`new-lp`).
 *
 * Quatro adaptações, e nenhuma é cosmética:
 *
 * 1. **par de tokens PRÓPRIO (`--footer`), que não inverte entre os temas.**
 *    É o único assim no app, e é deliberado: o rodapé fecha a página como uma
 *    faixa escura no light E no dark. Nenhum token semântico serviria —
 *    `background` e `foreground` trocam de lado, e `primary` vira rosa claro
 *    no escuro. A hierarquia sai por opacidade sobre uma base de contraste
 *    conhecido, o que é seguro aqui e não seria sobre superfície variável;
 * 2. **tipografia menor** que a da LP: aqui o rodapé fecha uma área logada, e
 *    não é a última chamada de uma landing page;
 * 3. **links em aba nova.** Quase todos são absolutos e apontam para seções da
 *    LP, que não são rotas deste app; os termos e a privacidade são rotas
 *    daqui (`/termos`, `/privacidade`), mas abrem em aba nova pelo mesmo motivo.
 *    Abrir na mesma aba tiraria a pessoa do meio do carrinho para ler uma
 *    política;
 * 4. **sem os `data-ph-*`.** Eles alimentam o PostHog da LP, que não existe
 *    aqui — atributos que ninguém lê viram ruído que o próximo leitor tenta
 *    entender.
 *
 */
export function Footer() {
  const ano = new Date().getFullYear();

  return (
    <footer className="mt-16 bg-footer px-6 pb-8 pt-12 text-footer-foreground md:px-10">
      <div className="mx-auto w-full max-w-6xl">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4">
          <div className="lg:col-span-2">
            <Logo className="mb-5 h-5 w-auto text-footer-foreground" />
            <p className="text-xs leading-relaxed text-footer-foreground/60">
              Rua Texas, 111 — Jardim Rancho Alegre
              <br />
              Santana de Parnaíba - SP — CEP 06515-200
            </p>

            <ul className="mt-6 flex items-center gap-3">
              {REDES.map(({ label, href, Icon }) => (
                <li key={label}>
                  {/* 44px de alvo, como o resto do app — o círculo de 40px da
                      LP fica abaixo do mínimo que o guia daqui exige. */}
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className="flex h-11 w-11 items-center justify-center rounded-full border border-footer-foreground/25 bg-footer-foreground/10 text-footer-foreground/80 transition-colors hover:bg-footer-foreground/20 hover:text-footer-foreground"
                  >
                    <Icon size={20} aria-hidden />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <nav aria-labelledby="rodape-menu">
            <h2 id="rodape-menu" className={TITULO}>
              Menu
            </h2>
            <ul className="space-y-3">
              {MENU.map((item) => (
                <li key={item.href}>
                  <a
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={LINK}
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <nav aria-labelledby="rodape-atendimento">
            <h2 id="rodape-atendimento" className={TITULO}>
              Atendimento
            </h2>
            <ul className="space-y-3">
              {ATENDIMENTO.map((item) => (
                <li key={item.href}>
                  <a
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={LINK}
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-3 border-t border-footer-foreground/15 pt-6 text-xs text-footer-foreground/60 md:flex-row">
          <p>© {ano} Mercado Único. Todos os direitos reservados.</p>
          <p>CNPJ: 51.337.445/0002-83</p>
        </div>
      </div>
    </footer>
  );
}
